import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, twoFactorAPI, scheduledReportsAPI, exportAPI } from '../lib/api';
import { formatDate, formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  RefreshCw,
  Settings as SettingsIcon,
  Users,
  Shield,
  Key,
  Bell,
  User,
  CheckCircle,
  XCircle,
  Sun,
  Moon,
  Smartphone,
  Download,
  Calendar,
  Plus,
  Trash,
  Clock,
  FileText,
  Mail,
  QrCode,
  Copy,
  Eye,
  EyeOff,
  Edit,
  Play,
  Pause,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Administrator' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'investigator', label: 'Investigator' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'viewer', label: 'Viewer' },
];

const REPORT_TYPES = [
  { value: 'daily_summary', label: 'Daily Summary' },
  { value: 'weekly_alerts', label: 'Weekly Alerts Report' },
  { value: 'monthly_cases', label: 'Monthly Cases Report' },
];

export default function Settings() {
  const { user, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [preferences, setPreferences] = useState({
    notifications_enabled: true,
    email_notifications: true,
  });
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    department: user?.department || '',
  });
  
  // 2FA State
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [settingUp2FA, setSettingUp2FA] = useState(false);
  
  // Scheduled Reports State
  const [scheduledReports, setScheduledReports] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [newReport, setNewReport] = useState({
    name: '',
    report_type: 'daily_summary',
    schedule: '0 9 * * *',
    email: '',
  });

  useEffect(() => {
    if (hasRole(['admin', 'supervisor'])) {
      fetchUsers();
    }
    fetchPreferences();
    fetchScheduledReports();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userAPI.list();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await userAPI.getPreferences();
      setPreferences(response.data);
      setTheme(response.data.theme || 'dark');
      setTwoFAEnabled(response.data.totp_enabled || false);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const response = await scheduledReportsAPI.list();
      setScheduledReports(response.data || []);
    } catch (error) {
      console.error('Failed to fetch scheduled reports:', error);
    }
  };

  const handleThemeToggle = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Apply theme
    if (newTheme === 'light') {
      document.documentElement.classList.add('light-theme');
      document.documentElement.classList.remove('dark-theme');
    } else {
      document.documentElement.classList.add('dark-theme');
      document.documentElement.classList.remove('light-theme');
    }
    
    try {
      await userAPI.updatePreferences({ ...preferences, theme: newTheme });
      toast.success(`Switched to ${newTheme} mode`);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await userAPI.update(userId, { role: newRole });
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      await userAPI.update(userId, { is_active: !currentStatus });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await userAPI.update(user.id, profileData);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    setSettingUp2FA(true);
    try {
      const response = await twoFactorAPI.setup();
      setTwoFASecret(response.data);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setSettingUp2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    try {
      await twoFactorAPI.verify(twoFACode);
      setTwoFAEnabled(true);
      setTwoFASecret(null);
      setTwoFACode('');
      toast.success('2FA enabled successfully');
    } catch (error) {
      toast.error('Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    try {
      await twoFactorAPI.disable(twoFACode);
      setTwoFAEnabled(false);
      setTwoFACode('');
      toast.success('2FA disabled');
    } catch (error) {
      toast.error('Invalid verification code');
    }
  };

  // Scheduled Reports handlers
  const [editingReport, setEditingReport] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleCreateReport = async (e) => {
    e.preventDefault();
    try {
      await scheduledReportsAPI.create(newReport);
      toast.success('Scheduled report created');
      setShowReportModal(false);
      setNewReport({ name: '', report_type: 'daily_summary', schedule: '0 9 * * *', email: '' });
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to create scheduled report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled report?')) return;
    try {
      await scheduledReportsAPI.delete(reportId);
      toast.success('Scheduled report deleted');
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleToggleReport = async (reportId, currentStatus) => {
    try {
      await scheduledReportsAPI.update(reportId, { is_active: !currentStatus });
      toast.success(`Report ${currentStatus ? 'paused' : 'activated'}`);
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to update report status');
    }
  };

  const handleEditReport = async (e) => {
    e.preventDefault();
    if (!editingReport) return;
    try {
      await scheduledReportsAPI.update(editingReport.id, {
        name: editingReport.name,
        report_type: editingReport.report_type,
        schedule: editingReport.schedule,
        email: editingReport.email,
      });
      toast.success('Scheduled report updated');
      setShowEditModal(false);
      setEditingReport(null);
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  const handleRunNow = async (reportId) => {
    try {
      await scheduledReportsAPI.runNow(reportId);
      toast.success('Report execution triggered');
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to trigger report');
    }
  };

  const openEditModal = (report) => {
    setEditingReport({ ...report });
    setShowEditModal(true);
  };

  // Export handlers
  const handleExport = (type, format) => {
    const token = localStorage.getItem('token');
    const url = exportAPI[type](format);
    window.open(`${url}&token=${token}`, '_blank');
    toast.success(`Exporting ${type} as ${format.toUpperCase()}`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your account and system preferences
          </p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="glass flex-wrap">
          <TabsTrigger value="profile" className="gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="w-4 h-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="appearance" className="gap-2">
            <Sun className="w-4 h-4" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <Calendar className="w-4 h-4" />
            Scheduled Reports
          </TabsTrigger>
          <TabsTrigger value="exports" className="gap-2">
            <Download className="w-4 h-4" />
            Export Data
          </TabsTrigger>
          {hasRole(['admin', 'supervisor']) && (
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              User Management
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label className="label-mono">Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Full Name</Label>
                  <Input
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Department</Label>
                  <Input
                    value={profileData.department}
                    onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                    className="bg-black/20 border-white/10"
                    placeholder="e.g., Financial Intelligence Unit"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Role</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs uppercase">
                      {user?.role || 'viewer'}
                    </Badge>
                  </div>
                </div>
                <Button type="submit" className="mt-4">
                  Save Changes
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          {/* 2FA Card */}
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                Two-Factor Authentication
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-medium">2FA Status</p>
                  <p className="text-sm text-muted-foreground">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <Badge 
                  variant="outline" 
                  className={twoFAEnabled 
                    ? 'bg-green-500/10 text-green-400 border-green-400' 
                    : 'bg-red-500/10 text-red-400 border-red-400'
                  }
                >
                  {twoFAEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {!twoFAEnabled && !twoFASecret && (
                <Button onClick={handleSetup2FA} disabled={settingUp2FA} className="gap-2">
                  {settingUp2FA ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4" />
                  )}
                  Enable 2FA
                </Button>
              )}

              {twoFASecret && (
                <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-primary" />
                    <p className="text-white font-medium">Scan QR Code</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  
                  {/* QR Code placeholder - would use actual QR library */}
                  <div className="w-48 h-48 bg-white rounded-lg p-2 mx-auto">
                    <div className="w-full h-full bg-black/10 flex items-center justify-center">
                      <QrCode className="w-24 h-24 text-gray-400" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="label-mono">Or enter this code manually:</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={showSecret ? twoFASecret.secret : '••••••••••••••••'}
                        readOnly
                        className="bg-black/20 border-white/10 font-mono"
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setShowSecret(!showSecret)}
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(twoFASecret.secret);
                          toast.success('Secret copied to clipboard');
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="label-mono">Enter verification code:</Label>
                    <div className="flex gap-2">
                      <Input
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value)}
                        placeholder="000000"
                        maxLength={6}
                        className="bg-black/20 border-white/10 font-mono"
                      />
                      <Button onClick={handleVerify2FA}>Verify</Button>
                    </div>
                  </div>
                </div>
              )}

              {twoFAEnabled && (
                <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-sm text-muted-foreground">
                    To disable 2FA, enter your current verification code:
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      className="bg-black/20 border-white/10 font-mono max-w-[200px]"
                    />
                    <Button variant="destructive" onClick={handleDisable2FA}>
                      Disable 2FA
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Theme Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {theme === 'dark' ? (
                    <Moon className="w-6 h-6 text-primary" />
                  ) : (
                    <Sun className="w-6 h-6 text-yellow-400" />
                  )}
                  <div>
                    <p className="text-white font-medium">
                      {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {theme === 'dark' 
                        ? 'Easier on the eyes in low-light environments' 
                        : 'Better visibility in bright environments'
                      }
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleThemeToggle}
                  className="gap-2"
                  data-testid="theme-toggle-btn"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun className="w-4 h-4" />
                      Switch to Light
                    </>
                  ) : (
                    <>
                      <Moon className="w-4 h-4" />
                      Switch to Dark
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          {/* Email Templates Card */}
          <Card className="glass border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Email Templates</h3>
                    <p className="text-xs text-muted-foreground">
                      Customize the look and content of notification emails
                    </p>
                  </div>
                </div>
                <Link to="/settings/email-templates">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Edit className="w-4 h-4" />
                    Manage Templates
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Scheduled Reports
              </CardTitle>
              <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-2">
                    <Plus className="w-4 h-4" />
                    New Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass border-border">
                  <DialogHeader>
                    <DialogTitle>Create Scheduled Report</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateReport} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="label-mono">Report Name</Label>
                      <Input
                        value={newReport.name}
                        onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                        placeholder="e.g., Weekly Alert Summary"
                        className="bg-black/20 border-white/10"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-mono">Report Type</Label>
                      <Select
                        value={newReport.report_type}
                        onValueChange={(v) => setNewReport({ ...newReport, report_type: v })}
                      >
                        <SelectTrigger className="bg-black/20 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {REPORT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-mono">Schedule (Cron Expression)</Label>
                      <Select
                        value={newReport.schedule}
                        onValueChange={(v) => setNewReport({ ...newReport, schedule: v })}
                      >
                        <SelectTrigger className="bg-black/20 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="0 9 * * *">Daily at 9:00 AM</SelectItem>
                          <SelectItem value="0 9 * * 1">Weekly on Monday</SelectItem>
                          <SelectItem value="0 9 1 * *">Monthly on 1st</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-mono">Email (optional)</Label>
                      <Input
                        type="email"
                        value={newReport.email}
                        onChange={(e) => setNewReport({ ...newReport, email: e.target.value })}
                        placeholder={user?.email}
                        className="bg-black/20 border-white/10"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="ghost" onClick={() => setShowReportModal(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">Create Schedule</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {scheduledReports.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Last Run</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduledReports.map((report) => (
                      <TableRow key={report.id} className={`border-white/5 ${!report.is_active ? 'opacity-50' : ''}`}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleReport(report.id, report.is_active)}
                            className={report.is_active ? 'text-green-400 hover:text-green-300' : 'text-muted-foreground hover:text-white'}
                          >
                            {report.is_active ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                          </Button>
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          <div>
                            {report.name}
                            {!report.is_active && (
                              <Badge variant="outline" className="ml-2 text-[10px] text-muted-foreground">
                                Paused
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {REPORT_TYPES.find(t => t.value === report.report_type)?.label || report.report_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">
                          {report.schedule}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {report.last_run ? formatRelativeTime(report.last_run) : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border">
                              <DropdownMenuItem onClick={() => handleRunNow(report.id)} className="cursor-pointer">
                                <Play className="w-4 h-4 mr-2" />
                                Run Now
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditModal(report)} className="cursor-pointer">
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteReport(report.id)} 
                                className="cursor-pointer text-red-400 focus:text-red-400"
                              >
                                <Trash className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No scheduled reports</p>
                  <p className="text-sm text-muted-foreground/50 mt-1">
                    Create a schedule to receive automated reports
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Report Modal */}
          <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="glass border-border">
              <DialogHeader>
                <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
                  Edit Scheduled Report
                </DialogTitle>
              </DialogHeader>
              {editingReport && (
                <form onSubmit={handleEditReport} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="label-mono">Report Name</Label>
                    <Input
                      value={editingReport.name}
                      onChange={(e) => setEditingReport({ ...editingReport, name: e.target.value })}
                      placeholder="Daily Security Summary"
                      className="bg-black/20 border-white/10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">Report Type</Label>
                    <Select
                      value={editingReport.report_type}
                      onValueChange={(value) => setEditingReport({ ...editingReport, report_type: value })}
                    >
                      <SelectTrigger className="bg-black/20 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {REPORT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">Schedule (Cron)</Label>
                    <Input
                      value={editingReport.schedule}
                      onChange={(e) => setEditingReport({ ...editingReport, schedule: e.target.value })}
                      placeholder="0 9 * * *"
                      className="bg-black/20 border-white/10 font-mono"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Examples: "0 9 * * *" (daily 9am), "0 9 * * 1" (weekly Monday 9am)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">Email (optional)</Label>
                    <Input
                      type="email"
                      value={editingReport.email || ''}
                      onChange={(e) => setEditingReport({ ...editingReport, email: e.target.value })}
                      placeholder={user?.email}
                      className="bg-black/20 border-white/10"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Exports Tab */}
        <TabsContent value="exports">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Export Data
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Entities Export */}
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <User className="w-6 h-6 text-blue-400" />
                      <h4 className="text-white font-medium">Entities</h4>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => handleExport('entities', 'excel')}
                      >
                        <Download className="w-4 h-4" />
                        Excel
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => handleExport('entities', 'pdf')}
                      >
                        <FileText className="w-4 h-4" />
                        PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Transactions Export */}
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <FileText className="w-6 h-6 text-green-400" />
                      <h4 className="text-white font-medium">Transactions</h4>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => handleExport('transactions', 'excel')}
                      >
                        <Download className="w-4 h-4" />
                        Excel
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => handleExport('transactions', 'pdf')}
                      >
                        <FileText className="w-4 h-4" />
                        PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Cases Export */}
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <FileText className="w-6 h-6 text-purple-400" />
                      <h4 className="text-white font-medium">Cases</h4>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => handleExport('cases', 'excel')}
                      >
                        <Download className="w-4 h-4" />
                        Excel
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => handleExport('cases', 'pdf')}
                      >
                        <FileText className="w-4 h-4" />
                        PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Management Tab */}
        {hasRole(['admin', 'supervisor']) && (
          <TabsContent value="users">
            <Card className="glass">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </div>
                ) : (
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id} className="border-white/5">
                          <TableCell>
                            <div>
                              <p className="text-white font-medium">{u.full_name}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={u.role}
                              onValueChange={(value) => handleRoleChange(u.id, value)}
                              disabled={u.id === user?.id}
                            >
                              <SelectTrigger className="w-[130px] h-8 bg-black/20 border-white/10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {ROLES.map((role) => (
                                  <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                u.is_active
                                  ? 'bg-green-500/10 text-green-400 border-green-400'
                                  : 'bg-red-500/10 text-red-400 border-red-400'
                              }
                            >
                              {u.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {u.last_login ? formatRelativeTime(u.last_login) : 'Never'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStatusToggle(u.id, u.is_active)}
                              disabled={u.id === user?.id}
                            >
                              {u.is_active ? (
                                <XCircle className="w-4 h-4 text-red-400" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
